import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
  Typography,
  Divider,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },
  formControl: {
    margin: theme.spacing(1, 0),
    minWidth: "100%",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  fieldContainer: {
    width: "100%",
  },
  switchRow: {
    display: "flex",
    justifyContent: "space-between",
  },
  sectionDivider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

const FollowUpRuleSchema = Yup.object().shape({
  name: Yup.string().min(2, "Too Short!").required("Required"),
  whatsappId: Yup.number().required("Required"),
  hoursThreshold: Yup.number().min(1).max(720).required("Required"),
  quickAnswerId: Yup.number().required("Required"),
  funnelStageId: Yup.number().nullable(),
  resolveAfterClientHours: Yup.number().nullable().min(1).max(720),
});

const FollowUpRuleModal = ({ open, onClose, ruleId, onSave }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { whatsApps } = useContext(WhatsAppsContext);
  const [quickAnswers, setQuickAnswers] = useState([]);
  const [funnelStages, setFunnelStages] = useState([]);

  const initialState = {
    name: "",
    whatsappId: "",
    hoursThreshold: 24,
    quickAnswerId: "",
    funnelStageId: "",
    resolveAfterClientHours: "",
    active: true,
  };

  const [rule, setRule] = useState(initialState);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchQuickAnswers = async () => {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { all: true },
        });
        if (isMounted.current) {
          setQuickAnswers(data.quickAnswers);
        }
      } catch (err) {
        toastError(err);
      }
    };
    if (open) {
      fetchQuickAnswers();
    }
  }, [open]);

  useEffect(() => {
    const fetchFunnelStages = async () => {
      try {
        const { data } = await api.get("/kanban/stages");
        if (isMounted.current) {
          setFunnelStages(data);
        }
      } catch (err) {
        toastError(err);
      }
    };
    if (open) {
      fetchFunnelStages();
    }
  }, [open]);

  useEffect(() => {
    const fetchRule = async () => {
      if (!ruleId) {
        setRule(initialState);
        return;
      }

      try {
        const { data } = await api.get(`/followUpRules/${ruleId}`);
        if (isMounted.current) {
          setRule({
            name: data.name,
            whatsappId: data.whatsappId,
            hoursThreshold: data.hoursThreshold,
            quickAnswerId: data.quickAnswerId,
            funnelStageId: data.funnelStageId || "",
            resolveAfterClientHours: data.resolveAfterClientHours || "",
            active: data.active,
          });
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchRule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId, open]);

  const handleClose = () => {
    onClose();
    setRule(initialState);
  };

  const handleSaveRule = async (values) => {
    const payload = {
      ...values,
      funnelStageId: values.funnelStageId || null,
      resolveAfterClientHours: values.resolveAfterClientHours || null,
    };
    try {
      if (ruleId) {
        await api.put(`/followUpRules/${ruleId}`, payload);
      } else {
        const { data } = await api.post("/followUpRules", payload);
        if (onSave) {
          onSave(data);
        }
      }
      toast.success(i18n.t("followUpRuleModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {ruleId
            ? i18n.t("followUpRuleModal.title.edit")
            : i18n.t("followUpRuleModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={rule}
          enableReinitialize={true}
          validationSchema={FollowUpRuleSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveRule(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, handleChange }) => (
            <Form>
              <DialogContent dividers>
                <Typography variant="caption" color="textSecondary">
                  {i18n.t("followUpRuleModal.helper")}
                </Typography>
                <div className={classes.fieldContainer}>
                  <Field
                    as={TextField}
                    label={i18n.t("followUpRuleModal.form.name")}
                    name="name"
                    autoFocus
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    fullWidth
                  />
                </div>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                  error={touched.whatsappId && Boolean(errors.whatsappId)}
                >
                  <InputLabel>
                    {i18n.t("followUpRuleModal.form.whatsapp")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("followUpRuleModal.form.whatsapp")}
                    name="whatsappId"
                  >
                    {whatsApps.map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        {whatsapp.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                >
                  <InputLabel>
                    {i18n.t("followUpRuleModal.form.funnelStage")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("followUpRuleModal.form.funnelStage")}
                    name="funnelStageId"
                  >
                    <MenuItem value="">
                      {i18n.t("followUpRuleModal.form.funnelStageAny")}
                    </MenuItem>
                    {funnelStages.map((stage) => (
                      <MenuItem key={stage.id} value={stage.id}>
                        {stage.funnel ? `${stage.funnel.name} — ${stage.name}` : stage.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
                <Divider className={classes.sectionDivider} />
                <Typography variant="subtitle2">
                  {i18n.t("followUpRuleModal.form.reminderSection")}
                </Typography>
                <div className={classes.fieldContainer}>
                  <Field
                    as={TextField}
                    type="number"
                    label={i18n.t("followUpRuleModal.form.hoursThreshold")}
                    name="hoursThreshold"
                    error={
                      touched.hoursThreshold && Boolean(errors.hoursThreshold)
                    }
                    helperText={
                      (touched.hoursThreshold && errors.hoursThreshold) ||
                      i18n.t("followUpRuleModal.form.hoursThresholdHelper")
                    }
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    fullWidth
                  />
                </div>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                  error={
                    touched.quickAnswerId && Boolean(errors.quickAnswerId)
                  }
                >
                  <InputLabel>
                    {i18n.t("followUpRuleModal.form.quickAnswer")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("followUpRuleModal.form.quickAnswer")}
                    name="quickAnswerId"
                  >
                    {quickAnswers.map((quickAnswer) => (
                      <MenuItem key={quickAnswer.id} value={quickAnswer.id}>
                        {quickAnswer.shortcut}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
                <Divider className={classes.sectionDivider} />
                <Typography variant="subtitle2">
                  {i18n.t("followUpRuleModal.form.resolveSection")}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {i18n.t("followUpRuleModal.form.resolveHelper")}
                </Typography>
                <div className={classes.fieldContainer}>
                  <Field
                    as={TextField}
                    type="number"
                    label={i18n.t(
                      "followUpRuleModal.form.resolveAfterClientHours"
                    )}
                    name="resolveAfterClientHours"
                    error={
                      touched.resolveAfterClientHours &&
                      Boolean(errors.resolveAfterClientHours)
                    }
                    helperText={
                      touched.resolveAfterClientHours &&
                      errors.resolveAfterClientHours
                    }
                    placeholder={i18n.t(
                      "followUpRuleModal.form.resolveAfterClientHoursPlaceholder"
                    )}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    fullWidth
                  />
                </div>
                <div className={classes.switchRow}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.active}
                        onChange={handleChange}
                        name="active"
                        color="primary"
                      />
                    }
                    label={i18n.t("followUpRuleModal.form.active")}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("followUpRuleModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {ruleId
                    ? i18n.t("followUpRuleModal.buttons.okEdit")
                    : i18n.t("followUpRuleModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FollowUpRuleModal;
